import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Download, Eye, Edit, Trash2, FileText, 
  Calendar, Building, Hash, User, Filter, CheckCircle, XCircle, CreditCard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { toast } from 'react-toastify';
import paymentService from '../../services/paymentService';

const Payments = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await paymentService.getAllPayments({
        page: pagination.current,
        limit: 50,
        search: searchTerm
      });

      if (response.success) {
        const transformedData = (response.data || []).map(payment => ({
          ...payment,
          id: payment._id || payment.id
        }));
        setData(transformedData);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        toast.error('Failed to load payments');
      }
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredData = data.filter(item =>
    item.paymentNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Table columns configuration
  const columns = [
    {
      key: 'paymentNumber',
      label: 'Payment #',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-green-600" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'paymentDate',
      label: 'Payment Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500" />
          <span>{new Date(value).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.customerCode}</div>
        </div>
      )
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-gray-500" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'paymentAmount',
      label: 'Amount',
      sortable: true,
      render: (value, row) => (
        <span className="font-semibold text-gray-900">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: row.currency || 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(value || 0)}
        </span>
      )
    },
    {
      key: 'amountInINR',
      label: 'In INR',
      sortable: true,
      render: (value, row) => {
        const code = row.currency || 'INR'
        if (code === 'INR') {
          return (
            <span className="font-medium text-gray-700">
              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.paymentAmount || 0)}
            </span>
          )
        }
        if (!value) {
          return <span className="text-amber-700">Rate not entered</span>
        }
        const rate = Number(row.conversionRate)
        return (
          <span className="font-semibold text-gray-900">
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}
            {rate > 0 ? <span className="block text-xs font-normal text-gray-500">1 {code} = ₹{rate}</span> : null}
          </span>
        )
      }
    },
    {
      key: 'paymentMethodName',
      label: 'Payment Method',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
          'Cleared': { color: 'bg-green-100 text-green-800', icon: '✅' },
          'Bounced': { color: 'bg-red-100 text-red-800', icon: '❌' },
          'Cancelled': { color: 'bg-gray-100 text-gray-800', icon: '🚫' }
        };
        const config = statusConfig[value] || statusConfig['Pending'];
        
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <span>{config.icon}</span>
            {value}
          </span>
        );
      }
    }
  ];

  // Event handlers
  const handleAddNew = () => {
    navigate('/accounts/payments/new');
  };

  const handleView = (item) => {
    navigate(`/accounts/payments/view/${item.id}`);
  };

  const handleEdit = (item) => {
    navigate(`/accounts/payments/edit/${item.id}`);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      setLoading(true);
      const response = await paymentService.deletePayment(deleteItem.id);

      if (response.success) {
        toast.success('Payment deleted successfully');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
      } else {
        toast.error(response.message || 'Failed to delete payment');
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    toast.info('Export functionality coming soon');
  };

  // Only Pending payments can be edited or deleted. Cleared / Bounced / Cancelled are read-only (report view).
  const canModifyPayment = (row) => row.status === 'Pending';

  const getPaymentActions = (row) => {
    const actions = [
      { type: 'view', label: 'View Details', onClick: () => handleView(row) }
    ];
    if (canModifyPayment(row)) {
      actions.push({ type: 'edit', label: 'Edit', onClick: () => handleEdit(row) });
      actions.push({ type: 'delete', label: 'Delete', onClick: () => handleDelete(row) });
    }
    return actions;
  };

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Accounts',
      path: '/accounts',
      onClick: () => navigate('/accounts')
    },
    {
      label: 'Transactions',
      path: '/accounts?tab=transactions',
      onClick: () => navigate('/accounts?tab=transactions')
    },
    {
      label: 'Payments',
      path: '/accounts/payments',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Payments</h1>
          <p>Track and manage all customer payments received</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add Payment
          </button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        customActions={getPaymentActions}
        onExport={handleExport}
        noCard={true}
        className="payments-table"
        pageSize={50}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteItem(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Payment"
        message={`Are you sure you want to delete payment ${deleteItem?.paymentNumber}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Payments;

